/*
 * Pro Carer Circle — shared content data for the category pages.
 * Extracted from the pre-restructure ProCarerCircleHub.jsx verbatim
 * (no rewriting) so it can be reused across dedicated category routes
 * instead of one long page.
 */
import {
  ShieldCheck,
  PoundSterling,
  Receipt,
  AlertOctagon,
  Ban,
  Bed,
  HomeIcon,
  CalendarClock,
  ClipboardList,
  Clock,
  BookOpen,
  FileText,
  Compass,
  HandHelping,
  MessageSquare,
  HelpCircle,
  AlertTriangle,
  Info,
} from "lucide-react";
import { Para, H4, Bullets, Callout } from "../components/workspace/HubText";
import { NATIVE_FORM_IDS, nativeFormPath } from "./nativeFormTemplateIds";

/* ════════════════════════════════════════════════════════════════════════
   PAYMENT PROTECTION — 6 items
   ════════════════════════════════════════════════════════════════════════ */
export const PAYMENT_ITEMS = [
  {
    id: "responsible-payer",
    icon: ShieldCheck,
    title: "Responsible Payer Confirmation",
    summary: "Confirms in writing who is legally and financially responsible for paying you.",
    download: { href: nativeFormPath(NATIVE_FORM_IDS.RESPONSIBLE_PAYER_CONFIRMATION), label: "Open in JCC Forms" },
    body: (
      <>
        <Para><strong>Purpose.</strong> This document confirms who is legally and financially responsible for paying your fees. It is especially important where the person receiving care is not managing their own finances (for example due to dementia, disability, or power of attorney arrangements).</Para>
        <H4>When to use</H4>
        <Bullets color="text-purple" items={["Before care begins", "At assessment stage or alongside your booking agreement", "Whenever payment will be made by someone other than the client"]} />
        <H4>How to use it</H4>
        <Bullets color="text-purple" items={[
          "Ask the family/client who will be responsible for payments.",
          "Complete the form with the responsible payer’s full details.",
          "Ensure the responsible payer signs and dates the document.",
          "Keep a copy with your records (digital or printed).",
        ]} />
        <Callout tone="purple" title="Why this matters">
          This form protects you from disputes later. If payment issues arise, it provides written confirmation of who agreed to pay you — helping you avoid “I thought the council / sibling / client was paying” situations.
        </Callout>
        <Callout tone="slate" title="Good practice tip" icon={Info}>
          If there are multiple family members, make sure the person signing has the authority to commit financially.
        </Callout>
      </>
    ),
  },
  {
    id: "deposit-payment",
    icon: PoundSterling,
    title: "Deposit and Payment Agreement",
    summary: "Sets clear expectations around deposits, payment schedules and late payments.",
    download: { href: nativeFormPath(NATIVE_FORM_IDS.DEPOSIT_PAYMENT_AGREEMENT), label: "Open in JCC Forms" },
    body: (
      <>
        <Para><strong>Purpose.</strong> This agreement sets clear expectations around deposits, payment schedules and what happens if payments are late or missed.</Para>
        <H4>When to use</H4>
        <Bullets color="text-purple" items={["Before the first shift or placement", "Particularly important for live-in, respite or block bookings"]} />
        <H4>How to use it</H4>
        <Bullets color="text-purple" items={[
          "Agree the deposit amount and payment terms verbally first.",
          "Complete the agreement with agreed dates, amounts and payment method.",
          "Ensure the client or responsible payer signs before care starts.",
          "Collect the deposit before the first day of care where possible.",
        ]} />
        <Callout tone="purple" title="Why this matters">
          Deposits reduce last-minute cancellations and protect your income. This agreement makes payment terms transparent and professional — not awkward.
        </Callout>
        <Callout tone="slate" title="Good practice tip" icon={Info}>
          Never feel uncomfortable requesting a deposit. This is standard practice in many professions and signals that you run a professional service, not a favour.
        </Callout>
      </>
    ),
  },
  {
    id: "invoicing-guidance",
    icon: Receipt,
    title: "Invoicing Guidance for Independent Carers",
    summary: "How to invoice confidently and consistently as a self-employed carer.",
    download: { href: "https://drive.google.com/file/d/1fTVKjj5WIYQgvrBZNLLC_JWYBQDKHqPL/view?usp=drive_link", label: "Download invoicing guide & template" },
    body: (
      <>
        <Para>Clear, professional invoicing protects your income, your time and your working relationship with clients. You don’t need to be an accountant — you just need a clear system.</Para>
        <H4>Why proper invoicing matters</H4>
        <Bullets color="text-purple" items={[
          "Sets clear expectations around payment",
          "Reduces late or missed payments",
          "Creates a professional boundary",
          "Provides evidence if disputes arise",
          "Helps with tax records and returns",
        ]} />
        <Para>Many payment issues start not with bad intent — but with unclear invoices.</Para>
        <H4>What every carer invoice should include</H4>
        <Para><strong>Your details:</strong> full name, business name (if applicable), address, email and phone.</Para>
        <Para><strong>Client details:</strong> client name, address where care was provided, responsible payer name (if different).</Para>
        <Para><strong>Invoice details:</strong> unique invoice number, invoice date, care period covered (e.g. 1–14 March 2026).</Para>
        <Para><strong>Care description:</strong> type of care provided (live-in / hourly / respite), dates and hours/days worked, agreed rate.</Para>
        <Para><strong>Payment summary:</strong> subtotal, any deposit already paid, balance due, payment due date.</Para>
        <Para><strong>Payment instructions:</strong> bank details or agreed payment method and a reference (usually the invoice number).</Para>
        <H4>When to invoice</H4>
        <Bullets color="text-purple" items={[
          "Live-in care — weekly or fortnightly; in advance where possible; always agreed in writing before placement starts.",
          "Hourly or visiting care — weekly or monthly; use a clear cut-off date.",
          "Respite or short-term care — immediately after care ends, or in advance where appropriate.",
        ]} />
        <H4>Deposits &amp; advance payments</H4>
        <Bullets color="text-purple" items={[
          "Take a deposit before care begins",
          "Clearly state how the deposit is used (e.g. deducted from final invoice)",
          "Confirm whether it is refundable or non-refundable",
        ]} />
        <H4>Payment terms (keep them simple)</H4>
        <Bullets color="text-purple" items={["Due on receipt", "7 days from invoice date", "14 days from invoice date"]} />
        <Para>Avoid vague terms like “as soon as possible”. Your invoice should clearly state: <em>Payment due by: [DATE]</em>.</Para>
        <H4>Late or missing payments</H4>
        <Bullets color="text-purple" items={[
          "Check the due date — don’t chase early",
          "Send a polite reminder",
          "Follow up in writing if unpaid",
          "Refer to your agreed terms",
          "Pause or end care if necessary (using JCC guidance)",
        ]} />
        <H4>Record keeping &amp; tax</H4>
        <Bullets color="text-purple" items={["All invoices sent", "Proof of payments received", "Any written payment agreements"]} />
        <Para>Digital copies are perfectly acceptable.</Para>
        <H4>Common mistakes to avoid</H4>
        <Bullets color="text-amber-700" icon={AlertOctagon} items={[
          "No invoice number",
          "Vague descriptions like “care work”",
          "No payment deadline",
          "Invoicing different clients differently without explanation",
          "Letting unpaid invoices build up",
        ]} />
        <Callout tone="purple" title="A note on confidence">
          Asking for payment is not awkward — it’s part of running a professional service. You are providing skilled, responsible, emotionally demanding work. Clear invoicing protects both you and the client.
        </Callout>
      </>
    ),
  },
  {
    id: "late-payment",
    icon: AlertOctagon,
    title: "Late or Non-Payment Response Guidance",
    summary: "A staged, professional way to respond if payment doesn’t arrive.",
    download: { href: "https://drive.google.com/file/d/1nRUMCHp7VIO4ihER25Q4Lm8QhQ1r8hx9/view?usp=drive_link", label: "Download response guidance" },
    extraDownloads: [
      { href: "https://drive.google.com/file/d/1DmJ-PUZ2_TH1znPZTqxIH7k2sRc7phKn/view?usp=drive_link", label: "Final notice – outstanding payment template" },
    ],
    body: (
      <>
        <Para>Late or missed payments are stressful — especially in care, where emotional labour and financial reality collide. This guide helps you respond step by step, without burning bridges or burning yourself out.</Para>
        <H4>Stage 1 — Initial reminder (1–5 days late)</H4>
        <Bullets color="text-purple" items={[
          "Send the Late Payment Reminder Template",
          "Keep the tone polite and factual",
          "Do not apologise for asking",
          "Save a copy of the message",
        ]} />
        <Para>Many late payments are genuinely accidental — this stage often resolves things.</Para>
        <H4>Stage 2 — Firm follow-up (7–14 days late)</H4>
        <Para>If there’s no response or no payment, send a second written message referring clearly to the invoice number, due date and agreed payment terms. Tone: calm, firm, professional. Key message: <em>payment is now overdue and needs to be resolved promptly to avoid disruption to care.</em> You are setting boundaries, not threatening.</Para>
        <H4>Stage 3 — Formal notice &amp; boundary setting</H4>
        <Bullets color="text-purple" items={[
          "State clearly that payment is overdue",
          "Give a final payment deadline (e.g. 48–72 hours)",
          "Explain the consequence of continued non-payment",
        ]} />
        <Para><em>“If payment is not received by [date], I will need to pause care in line with my agreed terms.”</em> This is not punitive — it is professional self-protection.</Para>
        <H4>Stage 4 — Pausing or ending care</H4>
        <Para>If payment is still not made, you are within your rights to pause care or end the placement (except in genuine emergency safeguarding situations). Communicate in writing, keep the message factual and unemotional, do not argue or justify excessively. <em>“Due to continued non-payment, I am unable to continue providing care beyond [date].”</em></Para>
        <H4>Always keep records</H4>
        <Bullets color="text-purple" items={["Invoices", "Reminder emails/messages", "Payment agreements", "Bank statements showing non-payment"]} />
        <H4>Common traps to avoid</H4>
        <Bullets color="text-amber-700" icon={AlertOctagon} items={[
          "Waiting too long to act",
          "Feeling guilty for enforcing payment",
          "Continuing to work unpaid",
          "Relying on verbal promises only",
          "Allowing arrears to build up",
        ]} />
        <Callout tone="purple" title="JCC reassurance">
          Most carers struggle with this not because they’re unprofessional, but because they’re kind. Clear systems protect your kindness from being exploited.
        </Callout>
      </>
    ),
  },
  {
    id: "pause-or-end-care",
    icon: Ban,
    title: "When to Pause or End Care Due to Non-Payment",
    summary: "Formal guidance on the last resort — paused calmly and lawfully.",
    download: { href: "https://drive.google.com/file/d/1DmJ-PUZ2_TH1znPZTqxIH7k2sRc7phKn/view?usp=drive_link", label: "Download formal guidance" },
    body: (
      <>
        <Para>Providing care without being paid is not sustainable, fair or professional. This guidance explains when and how an independent carer may pause or end care due to non-payment, while remaining ethical, calm and compliant.</Para>
        <H4>Core principle</H4>
        <Para>Independent carers are self-employed professionals, not volunteers. You are entitled to be paid for work completed, to protect your income, and to set and enforce reasonable boundaries. Pausing or ending care due to non-payment is a last resort, but it is a legitimate one.</Para>
        <H4>When it is appropriate to pause or end care</H4>
        <Bullets color="text-purple" items={[
          "An invoice has passed its due date",
          "Payment reminders have been sent",
          "No payment or meaningful response has been received",
          "Arrears are increasing",
          "Promises to pay have not been honoured",
          "Continued work would place you at financial risk",
        ]} />
        <H4>Recommended escalation timeline</H4>
        <Bullets color="text-purple" items={[
          "1️⃣ Invoice issued — clear invoice, payment terms, due date.",
          "2️⃣ Late payment reminder — polite, factual, opportunity to resolve.",
          "3️⃣ Final notice — clear deadline, consequences explained, in writing.",
          "4️⃣ Pause or end care — only if payment still outstanding; calm, clear, appropriate notice where possible.",
        ]} />
        <H4>Ethical &amp; safeguarding considerations</H4>
        <Bullets color="text-purple" items={[
          "Non-payment alone does not create an obligation to continue unpaid work",
          "You are not responsible for funding care",
          "Families remain responsible for arranging alternative care if payment fails",
        ]} />
        <Para>In emergency situations follow safeguarding procedures — this does not mean continuing unpaid care indefinitely. Document your actions clearly.</Para>
        <H4>How to communicate the decision</H4>
        <Bullets color="text-purple" items={[
          "Use written communication",
          "Be factual, not emotional",
          "Refer to invoices and dates",
          "Avoid blame or argument",
          "Keep the message short and clear",
        ]} />
        <H4>Common myths (and the truth)</H4>
        <Para><em>“I can’t stop care — it would be unethical.”</em> It is unethical to expect unpaid labour.</Para>
        <Para><em>“I should give them more time.”</em> Reasonable time is fair. Unlimited time is risky.</Para>
        <Para><em>“This will make me look unprofessional.”</em> Clear boundaries are professionalism.</Para>
        <Para><em>“Agencies wouldn’t allow this.”</em> Agencies suspend care for non-payment routinely.</Para>
        <Callout tone="purple" title="A reassuring note">
          Many carers delay action because they are kind, conscientious and deeply human. This guidance exists to help you act earlier, act calmly, and act without guilt. Boundaries do not make you uncaring. They make caring sustainable.
        </Callout>
      </>
    ),
  },
  {
    id: "cash-payments",
    icon: PoundSterling,
    title: "Cash Payments — Important Guidance",
    summary: "Why traceable payment methods are strongly preferred — and what to do if cash is accepted.",
    download: { href: "https://drive.google.com/file/d/1k9t8ZUlRMVhOP5sbH3AyDzPdqDfA6UKP/view?usp=drive_link", label: "Cash receipt template" },
    body: (
      <>
        <Para>While some carers accept cash payments, non-cash methods are strongly recommended wherever possible.</Para>
        <Bullets color="text-amber-700" icon={AlertOctagon} items={[
          "Cash payments cannot always be reliably proven",
          "Cash leaves no independent payment trail",
          "Cash can create disputes if a payment is later denied",
          "Cash offers less protection for both carer and client",
        ]} />
        <Para>For this reason, Just Care Community encourages carers to use bank transfer or other traceable payment methods, which provide clear records and protection should any disagreement arise.</Para>
        <H4>If cash is accepted by prior agreement</H4>
        <Bullets color="text-purple" items={[
          "Issue a written receipt immediately",
          "Record the payment against the relevant invoice",
          "Keep clear records of the date and amount received",
        ]} />
        <Callout tone="slate" title="Record-keeping note" icon={Info}>
          Cash receipts should be kept with the related invoice, recorded against income records, and retained for tax and dispute purposes.
        </Callout>
      </>
    ),
  },
];

/* ════════════════════════════════════════════════════════════════════════
   CONTRACTS & AGREEMENTS — 8 items
   (Emergency Safeguarding Measures moved to SAFETY_ITEMS below, per the
   Safety & Escalation category spec.)
   ════════════════════════════════════════════════════════════════════════ */
export const CONTRACT_ITEMS = [
  {
    id: "live-in-agreement",
    icon: Bed,
    title: "Live-In Carer Agreement",
    summary: "Defines the specific terms of a live-in arrangement — hours, rest, accommodation, household boundaries.",
    download: { href: "https://drive.google.com/file/d/1zcBkQVUok8lfdm0KqvQg0qfl1H3WK55S/view?usp=drive_link", label: "Download Live-In Agreement (PDF, fillable)" },
    body: (
      <>
        <Para><strong>Purpose.</strong> The Live-In Carer Agreement sets out the specific terms of a live-in care arrangement, where the carer both works and resides in the client’s home. Because live-in care involves shared space, routines and boundaries, this agreement is essential for ensuring the arrangement is safe, fair, sustainable and respectful for everyone involved.</Para>
        <H4>When to use</H4>
        <Bullets color="text-purple" items={[
          "For any placement where the carer sleeps in the client’s home",
          "For short-term respite or long-term live-in arrangements (a separate Respite Form is also available)",
          "Before the carer moves in or stays overnight",
          "Alongside: Care Booking Agreement, Responsible Payer Confirmation, Deposit & Payment Agreement",
        ]} />
        <H4>How to use it</H4>
        <Para>Complete the agreement before placement begins. Clearly define start date and expected duration, working hours and expected daily duties, agreed rest periods and breaks, overnight expectations (sleeping nights vs waking nights), fees and payment schedule, accommodation details.</Para>
        <H4>Accommodation details to confirm</H4>
        <Bullets color="text-purple" items={[
          "The carer has their own private bedroom",
          "Access to bathroom facilities",
          "Reasonable use of kitchen, laundry and shared areas",
          "Internet access (where applicable)",
        ]} />
        <H4>Household boundaries to clarify</H4>
        <Bullets color="text-purple" items={["Visitors", "Smoking / alcohol", "Pets", "Use of the carer’s personal time"]} />
        <H4>Signatures &amp; copies</H4>
        <Para>Signed and dated by the carer and the client or responsible payer. Keep copies for all parties.</Para>
        <Callout tone="purple" title="Why this matters">
          Live-in care can fail not because of poor care — but because of unclear expectations. A clear agreement protects the relationship as much as the carer.
        </Callout>
        <Callout tone="slate" title="Good practice tips" icon={Info}>
          Talk through the agreement openly — don’t just send it to be signed. If duties begin to expand beyond what was agreed, pause and revisit the document. If overnight care becomes more demanding, this should be formally reviewed and re-agreed. Trust your instincts — if something feels unreasonable, it probably is.
        </Callout>
      </>
    ),
  },
  {
    id: "arrival-confirmation",
    icon: HomeIcon,
    title: "Arrival Confirmation",
    summary: "A simple safety step completed as soon as you arrive at a new placement.",
    download: { href: "https://drive.google.com/file/d/1CChE-shDanat23ux_qZ1E5RHGXIeQsk2/view?usp=sharing", label: "Download Arrival Confirmation (print before placement)" },
    body: (
      <>
        <Para>Working independently means protecting yourself as well as your client. This Arrival Confirmation Form is a simple but important safety step that should be completed as soon as you arrive at a new placement.</Para>
        <H4>What to do on arrival</H4>
        <Bullets color="text-purple" items={[
          "Arrive at the agreed address and time",
          "Complete the Arrival Confirmation Form",
          "Sign it together with the client or family representative",
          "Share a copy with the family",
          "Send a brief message to your chosen safety contact (e.g. “Arrived safely at placement, all good.”)",
          "Keep a copy for your own records",
        ]} />
        <H4>Why this matters</H4>
        <Bullets color="text-purple" items={[
          "Confirms to the family that the correct carer has arrived",
          "Protects you if there is ever a dispute about start time or attendance",
          "Ensures someone knows where you are when working alone",
          "Keeps the start of a placement clear and professional",
        ]} />
        <Callout tone="amber" title="Important boundary" icon={AlertTriangle}>
          JCC provides this form as a safety and professionalism tool only. JCC does not receive or store arrival confirmations, does not monitor placements and does not act as an emergency contact. This keeps you independent — and protected.
        </Callout>
      </>
    ),
  },
  {
    id: "respite-agreement",
    icon: CalendarClock,
    title: "Respite Care Agreement",
    summary: "For short-term, time-limited care arrangements.",
    download: { href: "https://drive.google.com/file/d/1HBzrXuQMc01DMplWzwntFKfaBzVX0219/view?usp=sharing", label: "Download Respite Agreement" },
    body: (
      <>
        <Para>This agreement supports short-term, temporary care arrangements, protecting both the carer and the family while keeping expectations clear and professional.</Para>
        <H4>When to use</H4>
        <Bullets color="text-purple" items={[
          "Short-term or time-limited care",
          "Covering planned or emergency breaks",
          "Filling in for a regular carer",
          "Supporting family carers during holidays, illness or recovery",
          "Temporary live-in or visiting care for a defined period",
        ]} />
        <Para>Typical examples: a family carer going away for a week; a regular carer taking leave; emergency cover following hospital discharge; trial or stop-gap care while long-term arrangements are explored. <strong>Not intended for ongoing or open-ended care.</strong></Para>
        <H4>Who should sign</H4>
        <Para>The carer providing the respite care, and the client or responsible payer. If the care recipient lacks capacity, the person legally or practically responsible for payments and decisions must sign.</Para>
        <H4>Timing</H4>
        <Para>Ideally completed before care begins and signed before arrival for live-in respite. In emergency situations, a verbal agreement may occur first, but the written agreement should follow as soon as practicable.</Para>
        <H4>Fees, deposits &amp; payment protection</H4>
        <Bullets color="text-purple" items={[
          "Set clear rates or fixed fees",
          "Request deposits or advance payment",
          "Protect yourself against last-minute cancellations",
        ]} />
        <H4>Scope of care — staying within role</H4>
        <Para>Respite care can feel informal. The agreement helps prevent “scope creep”, set realistic boundaries and protect carers from being asked to take on unsuitable tasks. Refer back to the agreement politely if expectations begin to shift.</Para>
        <H4>Carer withdrawal &amp; safety</H4>
        <Bullets color="text-purple" items={[
          "Conditions become unsafe",
          "Payment terms are breached",
          "Information provided was inaccurate or incomplete",
        ]} />
        <Callout tone="purple" title="Tone &amp; professionalism">
          Present the agreement confidently as standard professional practice, not as a sign of mistrust. Clear agreements lead to better care experiences — for carers, families and those receiving care.
        </Callout>
      </>
    ),
  },
  {
    id: "change-of-care",
    icon: ClipboardList,
    title: "Change of Care Terms",
    summary: "For when needs, hours, rates or responsibilities change.",
    download: { href: nativeFormPath(NATIVE_FORM_IDS.CHANGE_OF_CARE_TERMS), label: "Open in JCC Forms" },
    body: (
      <>
        <Para><strong>Purpose.</strong> Protects clarity, professionalism and fairness when care arrangements need to change. Care is dynamic — people’s needs evolve — and this document ensures changes are handled transparently.</Para>
        <H4>When to use it</H4>
        <Bullets color="text-purple" items={[
          "Rate increases (planned or necessary)",
          "Increased care needs or responsibilities",
          "Schedule or rota changes",
          "Transition from companionship to personal care",
          "Temporary adjustments longer than a few days",
        ]} />
        <Para>If it affects time, money or responsibility, it should be documented.</Para>
        <H4>Notice period guidance</H4>
        <Para>Minimum recommended: 7–14 days for most changes. Immediate changes may apply in safeguarding or health-critical situations, but should still be documented retrospectively.</Para>
        <H4>Tone &amp; communication</H4>
        <Bullets color="text-purple" items={[
          "Use calm, factual language",
          "Focus on sustainability and quality of care",
          "Avoid emotional or apologetic wording",
          "Keep explanations brief and respectful",
        ]} />
        <H4>If the client does not agree</H4>
        <Bullets color="text-purple" items={[
          "Refer back to your Care Booking Agreement",
          "Consider whether the placement remains sustainable",
          "Use the Withdrawal or Change Clause if necessary",
          "Document all communication",
        ]} />
        <Callout tone="purple" title="JCC standard">
          Always send this form in writing, keep a signed copy, store alongside the original care agreement and follow up with a short confirmation message. Clear paperwork = fewer problems later.
        </Callout>
      </>
    ),
  },
  {
    id: "schedule-expectations",
    icon: Clock,
    title: "Care Schedule & Expectations Template",
    summary: "A clarity and communication tool — not a legal document.",
    download: { href: nativeFormPath(NATIVE_FORM_IDS.CARE_SCHEDULE_EXPECTATIONS), label: "Open in JCC Forms" },
    body: (
      <>
        <Para>Designed to help carers and families start on the same page — not about control or bureaucracy, but clarity, fairness and preventing misunderstandings.</Para>
        <H4>Complete this together (ideally before care starts)</H4>
        <Para>Wherever possible, carer and client/family should complete this together. Use it as a conversation guide. If care has already started, complete it as soon as possible to confirm expectations.</Para>
        <H4>Be honest and realistic</H4>
        <Para>Agree what care looks like now, not what you hope it might become. Be clear about working hours, rest breaks, night-time expectations and boundaries around duties.</Para>
        <H4>Use it as a living document</H4>
        <Bullets color="text-purple" items={[
          "Care hours increase",
          "Health or mobility declines",
          "Night-time support changes",
          "Rest breaks are affected",
          "Additional people require care",
        ]} />
        <Para>If care changes significantly, complete a Change of Care form alongside this document.</Para>
        <H4>Keep a copy each</H4>
        <Para>Carer keeps one. Client/family keeps one. So everyone can refer back to what was agreed.</Para>
        <Callout tone="purple" title="What this template is — and isn’t">
          ✅ A clarity and communication tool · a professional boundary-setting document · a way to prevent disputes.<br />
          ❌ A replacement for a care contract · a legal document · a sign of mistrust.
        </Callout>
      </>
    ),
  },
  {
    id: "hourly-agreement",
    icon: Clock,
    title: "Hourly Care Agreement",
    summary: "For regular daily/weekly visits or short hourly care arrangements.",
    download: { href: "https://drive.google.com/file/d/157hjGGcjrmhV0ylJjjrvqTusc-YChN7t/view?usp=drive_link", label: "Download Hourly Agreement" },
    body: (
      <>
        <Para>Designed for self-employed carers providing regular or ad-hoc hourly care without an agency.</Para>
        <H4>When to use this agreement</H4>
        <Bullets color="text-purple" items={[
          "Regular daily or weekly visits",
          "Short visits (e.g. 1–4 hours at a time)",
          "Ongoing hourly care arrangements",
          "Private pay clients",
        ]} />
        <Para><strong>Do not use this for:</strong> live-in care, respite placements, or overnight/sleep-in arrangements (use the relevant JCC templates instead).</Para>
        <H4>Why this agreement matters</H4>
        <Bullets color="text-purple" items={[
          "Sets boundaries clearly",
          "Prevents misunderstandings",
          "Supports fair payment",
          "Gives carers confidence to say “this has changed”",
          "Protects relationships by avoiding awkward conversations later",
        ]} />
        <H4>Best practice tips</H4>
        <Bullets color="text-purple" items={[
          "Always complete this before care starts",
          "Walk through it together — don’t just hand it over",
          "Be honest about what you can and cannot provide",
          "Keep a signed copy for both parties",
          "Review it if care needs change",
        ]} />
      </>
    ),
  },
  {
    id: "standard-placement-guidelines",
    icon: BookOpen,
    title: "Standard Placement Guidelines",
    summary: "Shared expectations for placements — a framework for good communication, not a rulebook.",
    download: { href: "https://drive.google.com/file/d/12yk0XT_9HFuIy1uoZWrdxBjvZehaDdcg/view?usp=sharing", label: "Download Standard Placement Guidelines" },
    body: (
      <>
        <Para>Explains the shared expectations for care placements arranged through Just Care Community. Designed to support safe, fair and professional care, reduce misunderstandings, encourage early open conversations and protect both carers and families. Sits alongside — but does not replace — formal care agreements.</Para>
        <H4>Who should read this</H4>
        <Bullets color="text-purple" items={[
          "Carers before accepting a placement",
          "Families before confirming care",
          "Anyone involved in arranging or supporting a placement",
        ]} />
        <H4>When to use the guidelines</H4>
        <Bullets color="text-purple" items={[
          "Before care starts — to understand expectations",
          "During care — as a reference point if questions arise",
          "If circumstances change — to guide review conversations",
          "If concerns arise — around safety, boundaries or suitability",
        ]} />
        <Callout tone="purple" title="Important">
          JCC provides these guidelines so carers and families can make informed choices and work together professionally. Reading and understanding them is part of taking responsibility for a placement.
        </Callout>
      </>
    ),
  },
  {
    id: "care-plan",
    icon: FileText,
    title: "JCC Core Care Plan — What is a Care Plan?",
    summary: "The foundation of safe, person-centred care.",
    download: { href: "https://drive.google.com/file/d/1V3CINlyVNyd_k2hiVNIrMzo3DqaXob8Z/view?usp=drive_link", label: "Download Core Care Plan" },
    body: (
      <>
        <Para>The Care Plan is the foundation of safe, person-centred care — not just paperwork, but a working agreement between the carer and the client (or their representative). It covers the client’s needs, preferences and routines, any risks or safety considerations, and what support will be provided (and what won’t).</Para>
        <H4>Who should be involved?</H4>
        <Bullets color="text-purple" items={[
          "The client (if they have capacity)",
          "A family member or representative",
          "The carer providing support",
          "Any relevant professionals (e.g. nurse, social worker, GP)",
        ]} />
        <Para>The most important voice is always the client’s.</Para>
        <H4>Where should the information come from?</H4>
        <Bullets color="text-purple" items={[
          "Direct conversation with the client",
          "Input from family or next of kin",
          "Previous care notes (if available)",
          "Medical information (e.g. diagnoses, medications)",
          "Observation during initial visits",
        ]} />
        <Para>Never guess or assume — if something is unclear, clarify it before care begins.</Para>
        <H4>When should it be completed?</H4>
        <Para>Before care begins (or as early as possible during the first contact). It ensures care starts safely, prevents misunderstandings, protects both client and carer, and provides a clear record from day one. If a full plan cannot be completed immediately, a basic version should still be in place and updated as soon as possible.</Para>
        <H4>How should it be stored?</H4>
        <Bullets color="text-purple" items={[
          "Stored securely (in line with data protection/GDPR)",
          "Accessible to the carer when needed",
          "Shared only with those who need to see it",
        ]} />
        <Callout tone="purple" title="Why this matters">
          A well-completed Care Plan doesn’t just protect — it reassures everyone involved that care is being delivered safely, respectfully and with clarity from the very start.
        </Callout>
      </>
    ),
  },
];

/* ════════════════════════════════════════════════════════════════════════
   SAFETY & ESCALATION — moved out of Contracts & Agreements per the
   Safety & Escalation category spec (same underlying content, not rewritten)
   ════════════════════════════════════════════════════════════════════════ */
export const SAFETY_ITEMS = [
  {
    id: "emergency-safeguarding",
    icon: ShieldCheck,
    title: "Emergency Safeguarding Measures",
    summary: "Act quickly, lawfully and protectively when something goes seriously wrong.",
    download: { href: "https://drive.google.com/file/d/1y1gOdTKfoBzxWhkvbmB0sYk8t46OspWk/view?usp=sharing", label: "Download Safeguarding guidance" },
    body: (
      <>
        <Para>Safeguarding emergencies are rare — but when they happen, clarity saves people. These guidelines help you act quickly, lawfully and protectively — for your client, yourself and everyone involved.</Para>
        <H4>What counts as a safeguarding emergency?</H4>
        <Bullets color="text-amber-700" icon={AlertTriangle} items={[
          "Immediate danger to life or health",
          "Suspected physical, emotional, sexual or financial abuse",
          "Severe neglect (e.g. no food, medication withheld, unsafe living conditions)",
          "A medical emergency requiring urgent intervention",
          "A client being left alone when unsafe",
          "Serious deterioration in mental capacity or behaviour",
          "Threats of violence or intimidation",
          "You feel unsafe remaining in the placement",
        ]} />
        <Para>Trust your instincts. If something feels wrong, treat it as serious.</Para>
        <H4>Your first priority: safety</H4>
        <Para><strong>Immediate danger:</strong> Call emergency services (999 / 112). Do not delay to seek permission. Stay with the client if safe to do so.</Para>
        <Para><strong>Medical emergencies:</strong> Call NHS emergency services. Provide first aid only if trained. Follow medical instructions exactly.</Para>
        <H4>If abuse or neglect is suspected</H4>
        <Para>You do not need proof to raise a safeguarding concern. Ensure the client is safe, remove yourself from confrontation, do not investigate or accuse anyone. Report concerns to the local Adult Safeguarding Team, GP or health professional, or emergency services if risk is immediate.</Para>
        <H4>If you’re told “don’t tell anyone”</H4>
        <Para>“I care about your safety. I can’t promise to keep this secret if someone is at risk.” Never agree to secrecy where harm may be involved.</Para>
        <H4>If you need to leave the placement</H4>
        <Bullets color="text-purple" items={[
          "Ensure the client is not left alone if unsafe",
          "Inform emergency services or safeguarding team",
          "Document your actions",
          "Notify the family after safety is secured, if appropriate",
        ]} />
        <H4>Recording what happened</H4>
        <Para>As soon as possible, write: date &amp; time, what you saw or were told (facts only), actions you took, who you contacted. Avoid opinions or emotional language.</Para>
        <Callout tone="amber" title="JCC does not store care notes" icon={AlertTriangle}>
          Any notes you make remain your responsibility.
        </Callout>
        <Callout tone="purple" title="Key principle">
          If you’re unsure — escalate. You will never be criticised for protecting someone.
        </Callout>
      </>
    ),
  },
];

/* ════════════════════════════════════════════════════════════════════════
   WORKING INDEPENDENTLY — 5 long articles
   ════════════════════════════════════════════════════════════════════════ */
export const WORKING_ITEMS = [
  {
    id: "working-independently",
    icon: Compass,
    title: "Working Independently — What Changes?",
    summary: "Moving from agency work to independent care.",
    body: (
      <>
        <Para>Moving from agency work into independent care isn’t about doing more. It’s about doing things differently — and doing them properly. When you work independently, you’re no longer “assigned” to a client; you’re entering into a professional, adult-to-adult agreement — with clarity, boundaries and shared responsibility.</Para>

        <H4>1. You become the professional lead</H4>
        <Para>You set your availability, agree the scope of care, decide what you can — and cannot — provide, and speak directly with the family or client. This isn’t about power. It’s professional responsibility and mutual respect. You are not “helping out”. You are providing a skilled service.</Para>

        <H4>2. Clarity replaces assumptions</H4>
        <Para>Agency care often runs on assumptions (“It’s probably included”, “That’s just part of the job”). Independent care runs on clear agreements — written agreements, defined duties, change-of-care processes, payment clarity. Clarity protects everyone — including the client.</Para>

        <H4>3. Boundaries become essential (not optional)</H4>
        <Para>Kindness is still central, compassion still matters, flexibility still exists — but boundaries are no longer optional. You are allowed to say no to unsafe requests, pause care if terms are breached, revisit agreements when care needs change. Boundaries don’t make you “difficult”. They make care sustainable.</Para>

        <H4>4. Communication becomes adult, direct and ongoing</H4>
        <Para>There’s no agency buffer anymore — and that’s a good thing. Independent care relies on open conversations, early check-ins and honest updates when things shift. JCC templates support these conversations, not replace them. Paperwork doesn’t remove humanity — it protects it.</Para>

        <H4>5. Responsibility is shared — not carried alone</H4>
        <Para>One of the biggest myths about independent care is “you’re on your own now.” You’re not. What changes is that responsibility becomes shared and visible: families understand their role, payment expectations are clear, changes are documented, safeguarding is taken seriously. JCC exists to give you the infrastructure agencies never give carers.</Para>

        <H4>6. You are still you — just better supported</H4>
        <Para>Working independently doesn’t change your values, compassion, intuition or humanity. It simply gives you structure, protection and professional footing. You’re not becoming “corporate”. You’re becoming respected.</Para>

        <Callout tone="purple" title="Real-world protection">
          Working independently doesn’t add risk — it removes silent ones. Clear agreements and boundaries help protect carers from “just one more thing” becoming unpaid labour, from burnout caused by unclear expectations, from unsafe requests going unchallenged, from being blamed for changes they weren’t informed about, and from feeling guilty for enforcing reasonable boundaries. They also protect families from sudden withdrawals, misunderstandings, payment disputes and confusion during emergencies.
        </Callout>
        <Callout tone="slate" title="A JCC truth worth remembering" icon={Info}>
          Good care doesn’t rely on goodwill alone. It relies on clarity, communication and shared responsibility. That’s not cold. That’s kind.
        </Callout>
      </>
    ),
  },
  {
    id: "boundaries-without-guilt",
    icon: HandHelping,
    title: "Setting Boundaries Without Guilt",
    summary: "Boundaries are not the opposite of care — they make good care possible.",
    body: (
      <>
        <Para>Setting boundaries doesn’t make you cold. It makes you safe, sustainable and professional. Many carers struggle with boundaries because care work is built on kindness — but boundaries are not the opposite of care. They are what makes good care possible.</Para>

        <H4>Why carers feel guilty about boundaries</H4>
        <Para>If you’ve ever thought “I’ll just stay a bit longer”, “I don’t want to say no”, “they really need me”, “I should be able to cope” — you’re not weak. You’re human. Carers often feel guilt because you see vulnerability up close, build genuine emotional bonds, are used to putting others first and have been trained (explicitly or implicitly) to “go the extra mile”.</Para>
        <Para>But when “the extra mile” becomes the expected route, something breaks — usually you.</Para>

        <H4>What boundaries actually do</H4>
        <Bullets color="text-purple" items={[
          "Protect your energy",
          "Clarify expectations",
          "Prevent resentment",
          "Keep care arrangements fair",
          "Reduce misunderstandings",
          "Help families respect your role",
          "Make care safer for everyone",
        ]} />
        <Para>A carer with boundaries is not less caring. They are more reliable, more present, and more professional.</Para>

        <H4>Common boundary slips (and why they matter)</H4>
        <Bullets color="text-amber-700" icon={AlertOctagon} items={[
          "Working past agreed hours “just this once”",
          "Taking on extra tasks that weren’t discussed",
          "Missing breaks",
          "Being contacted late at night",
          "Being emotionally leaned on as a therapist or family mediator",
          "Feeling unable to say no to changes in care",
        ]} />
        <Para>Over time these lead to exhaustion, resentment, blurred roles, unsafe care and sudden breakdowns in placements.</Para>

        <H4>Reframing boundaries — a mindset shift</H4>
        <Para>Instead of “I’m letting them down”, try <strong>“I’m protecting the care arrangement.”</strong> Instead of “I should be able to cope”, try <strong>“This needs a conversation.”</strong> Instead of “I don’t want to seem difficult”, try <strong>“Clarity prevents conflict.”</strong></Para>

        <H4>What setting a boundary can sound like</H4>
        <Para><em>“I’m happy to help with that — let’s look at how it fits into the agreed care.”</em></Para>
        <Para><em>“That’s outside my role, but I can help you think through options.”</em></Para>
        <Para><em>“I need to stick to my break times so I can provide safe care.”</em></Para>
        <Para><em>“This feels like a change in care — let’s pause and review it properly.”</em></Para>
        <Para><em>“I’m not able to do that, but I want to be clear early rather than let it become an issue.”</em></Para>
        <Para>Clear — not unkind.</Para>

        <Callout tone="purple" title="JCC principle">
          Good care requires clear boundaries. Clear boundaries protect everyone involved.
        </Callout>
        <Callout tone="slate" title="Real-world protection" icon={Info}>
          Clear boundaries help protect you from role creep, burnout, unclear expectations, emotional over-dependence, unsafe care situations and sudden placement breakdowns. Boundaries don’t stop you being flexible — they stop flexibility turning into pressure.
        </Callout>
      </>
    ),
  },
  {
    id: "communicating-professionally",
    icon: MessageSquare,
    title: "Communicating Professionally With Families",
    summary: "Warm, clear, consistent communication is part of the care.",
    body: (
      <>
        <Para>Good care is built on trust — and trust is built through clear, calm, respectful communication. Professional doesn’t mean impersonal. What makes communication professional is how and when you communicate — not whether you care.</Para>
        <H4>Set the tone early</H4>
        <Para>The first few conversations matter more than you think. Speak calmly and confidently, use clear language (avoid jargon), be honest about what you can and can’t do, don’t overpromise to “be nice”. Clarity is kinder than people-pleasing.</Para>
        <Para><em>“I want to make sure expectations are clear from the beginning so this works well for everyone — including your mum.”</em> That sentence alone builds trust.</Para>
        <H4>Keep communication consistent</H4>
        <Bullets color="text-purple" items={[
          "Agree preferred contact method (text, WhatsApp, email)",
          "Agree on what counts as an update vs an emergency",
          "Keep notes factual, not emotional",
          "Avoid messaging late at night unless agreed",
        ]} />
        <H4>Stay factual, not personal</H4>
        <Para>Instead of <em>“She was very difficult today”</em> → try <em>“She appeared more agitated today and required additional reassurance during personal care.”</em> This protects you, the client, the family and the working relationship.</Para>
        <H4>Handling concerns or difficult conversations</H4>
        <Para>Be timely, specific, calm, focused on care impact (not emotion) and offer solutions where possible. <em>“I’ve noticed your dad is needing more support overnight. I wanted to flag this early so we can talk about whether the current care arrangement still meets his needs.”</em> This isn’t criticism. It’s professionalism.</Para>
        <H4>Boundaries are part of good communication</H4>
        <Bullets color="text-purple" items={[
          "“I’m not able to do that, but we can look at alternatives.”",
          "“That falls outside our agreed care tasks.”",
          "“Let’s revisit the care plan so expectations stay aligned.”",
        ]} />
        <H4>Written communication still counts</H4>
        <Para>Texts, notes, WhatsApps and emails are all part of your professional record. Before sending, ask: would I be comfortable reading this back? Is this clear and neutral? Does it reflect me as a professional carer? If emotions are running high — pause, draft, re-read, then send.</Para>
        <Callout tone="purple" title="Real-world protection">
          Calm, consistent, factual, documented communication creates a paper trail of professionalism. It protects you if a family later disputes what was agreed, expectations quietly shift, a concern is raised about care quality, a situation escalates emotionally, or you need to evidence that you acted responsibly. Professional words are not cold. They are protective.
        </Callout>
      </>
    ),
  },
  {
    id: "difficult-conversations",
    icon: HelpCircle,
    title: "Handling Difficult Conversations Without Guilt",
    summary: "Speak up early. Stay factual. Carry only the guilt that’s actually yours.",
    body: (
      <>
        <Para>Difficult conversations are part of caring — but feeling guilty about having them doesn’t have to be.</Para>
        <H4>Name the truth</H4>
        <Para>Having a difficult conversation does not mean you are uncaring, bad at your job, awkward or letting someone down. It usually means the opposite — you are paying attention, protecting the quality of care, respecting yourself and preventing bigger problems later.</Para>
        <H4>Why guilt shows up for carers</H4>
        <Para>Guilt creeps in when emotional care gets confused with unlimited availability, kindness gets mistaken for compliance, professionalism gets replaced with people-pleasing. You can care deeply and have boundaries. One does not cancel out the other.</Para>
        <H4>Reframe the purpose</H4>
        <Para>You’re not raising an issue to complain. You’re raising it to keep care safe, keep expectations realistic, protect the placement, prevent resentment and maintain professional standards. This isn’t confrontation — it’s course correction.</Para>
        <H4>A simple mindset shift</H4>
        <Para>Instead of “I hate bringing this up…” try <strong>“It’s my responsibility to be honest so care can work properly.”</strong></Para>
        <H4>How to approach the conversation</H4>
        <Bullets color="text-purple" items={[
          "Don’t wait until you’re overwhelmed — early conversations are easier conversations.",
          "Separate facts from feelings (e.g. “The care has increased since we started, particularly with overnight support and mobility.”)",
          "Use neutral, professional language (“I want to check we’re still aligned…”, “I need to flag something important.”)",
          "Avoid over-explaining — over-explaining is often guilt in disguise.",
          "Expect some discomfort — and allow it. Discomfort doesn’t equal danger.",
        ]} />
        <H4>If guilt hits after the conversation</H4>
        <Para>That’s normal. Guilt often shows up after you’ve done the right thing — especially if you’re used to putting yourself last. Remind yourself why you spoke, read the agreement again, ground yourself in facts, talk it through with another carer. Guilt is an emotion, not a verdict.</Para>
        <Callout tone="purple" title="Real-world protection">
          Most serious care breakdowns don’t start with conflict. They start with silence. Clear communication isn’t confrontation — it’s risk management, professionalism and good care practice in real life.
        </Callout>
      </>
    ),
  },
  {
    id: "knowing-when-to-say-no",
    icon: Ban,
    title: "Knowing When to Say No",
    summary: "Saying yes to everything doesn’t make you a better carer. It makes you a tired one.",
    body: (
      <>
        <Para>Most carers don’t struggle with doing the work. They struggle with knowing when to stop agreeing to more. Saying yes to everything doesn’t make you a better carer. It makes you a tired one.</Para>
        <H4>Why carers find “no” so hard</H4>
        <Bullets color="text-purple" items={[
          "You don’t want to let the client down",
          "You don’t want to upset the family",
          "You worry you’ll look unkind or unprofessional",
          "You tell yourself “it’s only this once”",
          "You’ve been conditioned to believe care = sacrifice",
        ]} />
        <Para>Care attracts big-hearted people. But big hearts still need boundaries.</Para>
        <H4>The quiet cost of always saying yes</H4>
        <Bullets color="text-amber-700" icon={AlertOctagon} items={[
          "Scope creep — extra tasks, longer hours, heavier care",
          "Emotional exhaustion",
          "Resentment (often followed by guilt for feeling it)",
          "Safety risks — for you and the client",
          "Burnout that ends a placement completely",
        ]} />
        <Para>Ironically, not saying no early often leads to having to walk away later.</Para>
        <H4>Knowing when “no” is the right answer</H4>
        <Bullets color="text-purple" items={[
          "The task wasn’t agreed and changes the care level",
          "You’re being asked to work unpaid or outside hours",
          "Your rest breaks are being eroded",
          "The care has become unsafe or unmanageable",
          "You feel pressure rather than discussion",
          "Your gut says “this isn’t right”",
        ]} />
        <Para>That gut feeling is professional instinct, not weakness.</Para>
        <H4>What saying no can sound like</H4>
        <Bullets color="text-purple" items={[
          "“That isn’t something I can take on, but we can discuss options.”",
          "“This would count as a change in care, so we need to review it properly.”",
          "“I’m not able to work beyond the agreed hours.”",
          "“For safety reasons, I can’t do that.”",
        ]} />
        <Callout tone="purple" title="A reframe worth keeping">
          Saying no protects the placement, keeps care sustainable, models professionalism and prevents crisis exits. Boundaries don’t reduce care. They protect it.
        </Callout>
      </>
    ),
  },
];

/* ════════════════════════════════════════════════════════════════════════
   TRAINING & CPD
   ════════════════════════════════════════════════════════════════════════ */
export const TRAINING_STEPS = [
  { n: 1, title: "Choose a training provider", body: "Select a reputable, accredited provider from the list below. Most offer individual courses, discounted bundles and annual access options." },
  { n: 2, title: "Register & purchase", body: "Create an account on the provider’s website and purchase the course(s) you need. You do not need to go through JCC — training is completed directly with the provider." },
  { n: 3, title: "Complete the course online", body: "Self-paced, short modules, accessible on phone, tablet or laptop. You can complete in stages — perfect for carers working long shifts." },
  { n: 4, title: "Download your certificate", body: "Course name, completion date, CPD hours/level, provider details. Save a copy for your records." },
  { n: 5, title: "Keep certificates accessible", body: "Store safely, label clearly, refresh key courses regularly. Pro members may wish to organise certificates within their JCC system." },
];

export const CORE_TRAINING = [
  "Safeguarding Adults (usually Level 2)",
  "Medication Awareness",
  "Moving & Handling (Theory)",
  "Infection Prevention & Control",
  "Food Hygiene / Food Safety (Level 2)",
  "Dementia Awareness",
];

export const PROVIDERS = [
  { name: "Florence Academy", body: "Clear, modern online courses for carers and health professionals. Medication Awareness, Safeguarding, Infection Control, Dementia Awareness. CPD-accredited, easy-to-download certificates, good for refresher training." },
  { name: "Caredemy", body: "Affordable online training with course bundles and annual access options. Core care, CPD top-ups, refreshers. Budget-friendly, instant certification, suitable for self-employed carers." },
  { name: "Social Care TV", body: "Long-established provider widely recognised in adult social care. Core compliance, Safeguarding, Care Certificate theory modules. Well recognised; broad course library; suitable for professional portfolios." },
  { name: "Redcrier Training Solutions", body: "Online and blended learning across health and social care. Safeguarding, care practice theory, specialist awareness. Quality-focused; suitable for ongoing professional development." },
  { name: "Skills for Health", body: "National organisation supporting workforce development in health and care. Care Certificate standards, framework-based learning. Recognised across the sector; a good reference point for standards." },
  { name: "eLearning for Healthcare (NHS)", body: "Free NHS-supported learning platform. Health awareness, professional development, knowledge refreshers. Free to access; excellent background learning; complements paid training." },
];

export const STARTER_PATH = [
  { n: 1, title: "Start with core training", body: "Begin with the most commonly expected courses. Many carers start with Safeguarding and Medication Awareness, then build from there." },
  { n: 2, title: "Choose one provider", body: "Pick one accredited provider and complete a small bundle rather than jumping between sites. Keeps certificates consistent, records easy to manage and costs predictable." },
  { n: 3, title: "Keep certificates organised", body: "Download each certificate, label clearly (e.g. “Safeguarding – 2026”) and keep them accessible in case a family asks." },
  { n: 4, title: "Match training to the care you provide", body: "Add specialist training where work calls for it — dementia, Parkinson’s, stroke, complex needs." },
  { n: 5, title: "Build gradually", body: "Add one or two courses each year. Refresh key training every 12–24 months. Build specialist knowledge over time." },
];

// Existing working destination for "Download CPD Tracker" — now the native
// JCC CPD Tracker form (Production template_id below), replacing the old
// Drive-hosted version.
export const CPD_TRACKER_HREF = nativeFormPath(NATIVE_FORM_IDS.CPD_TRACKER);

export const BUNDLES = [
  {
    id: "starter-bundle",
    title: "New Carer Starter Bundle",
    blurb: "The essential foundation every professional carer should have before stepping into a client’s home.",
    courses: ["Care Certificate (or equivalent foundation training)", "Safeguarding Adults", "Manual Handling", "Infection Control", "Basic First Aid", "Food Hygiene (if preparing meals)"],
    costs: ["Budget provider route: £165 – £225", "Mid-range recognised providers: £220 – £325"],
    note: "Face-to-face practical training (e.g. in-person moving & handling or first aid) may cost more but can strengthen your professional profile.",
    checklist: ["Safeguarding Adults completed", "Manual Handling (theory minimum)", "Infection Control", "Basic First Aid", "Understanding of documentation & reporting", "Knowledge of how to escalate concerns", "Clear agreement on sleep expectations and working hours"],
  },
  {
    id: "live-in-bundle",
    title: "Live-In Professional Bundle",
    blurb: "For carers working long shifts or sleep-ins. Live-in care requires more than kindness — it requires skill, knowledge and safe practice.",
    courses: ["Medication Administration", "Pressure Area Care", "Moving & Handling Advanced", "Record Keeping & Documentation", "End of Life Awareness", "Mental Capacity & DoLS overview"],
    costs: ["Budget providers: £115 – £180", "Mid-range recognised providers: £160 – £240"],
    note: "Face-to-face practical moving & handling training is strongly recommended for live-in carers.",
    checklist: ["Core foundation training completed", "Manual Handling (minimum theory; practical strongly advised)", "Medication awareness (even if not administering)", "Understanding of sleep vs waking night expectations", "Knowledge of escalation procedures", "Ability to document daily care safely", "Clear agreement on working hours and rest breaks"],
  },
  {
    id: "specialist-bundle",
    title: "Specialist Conditions Bundle",
    blurb: "For carers wanting to increase rates and competence. Specialist knowledge builds confidence — and justifies professional rates.",
    courses: ["Dementia Awareness & Behaviours", "Parkinson’s Support", "Diabetes Management", "Stroke Recovery Support", "Autism Awareness (adults)", "Palliative & End of Life Care"],
    costs: ["Budget route: £115 – £190", "Mid-range recognised providers: £160 – £245"],
    note: "Specialist training increases confidence, competence and professional credibility when supporting complex care needs.",
    checklist: ["Foundation training completed", "Understand the specific condition you are supporting", "Confident recognising deterioration or red flags", "Know escalation procedures and emergency pathways", "Clear about medication responsibilities", "Realistic boundaries around clinical tasks"],
  },
  {
    id: "growth-bundle",
    title: "Professional Growth Bundle",
    blurb: "Because staying in care is one thing. Building a sustainable career is another.",
    courses: ["Self-Employed Compliance Basics", "Record Keeping & GDPR Awareness", "Communication & Boundaries", "Conflict Resolution", "Time Management", "Self-Care & Burnout Prevention"],
    costs: ["Budget route (using free + low-cost providers): £40 – £90", "Mid-range recognised providers: £80 – £180"],
    note: "Professional development training strengthens credibility, supports rate positioning and protects long-term wellbeing.",
    checklist: ["Understand your employment status and tax responsibilities", "Know how to invoice correctly", "Understand GDPR and client confidentiality", "Confident setting working boundaries", "Know how to document and protect yourself", "Have a clear cancellation and payment policy", "Recognise early signs of burnout"],
  },
];
